import { createFileRoute, Link } from "@tanstack/react-router";
import { MapPin, ArrowRight, ShieldCheck, Clock, CheckCircle2 } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { CITY_CONTENT } from "@/lib/groepenkast-cities";
import dekkingOg from "@/assets/dekking-og.jpg.asset.json";

const SITE_URL = "https://elvion-group.nl";
const OG_IMAGE = `${SITE_URL}${dekkingOg.url}`;

export const Route = createFileRoute("/dekking")({
  head: () => {
    const url = `${SITE_URL}/dekking`;
    const title = "Groepenkast vervangen in heel Nederland | Dekking ELVION";
    const description =
      "Bekijk waar Elvion Group Nederland groepenkasten vervangt: Amsterdam, Rotterdam, Den Haag, Utrecht, Eindhoven, Almere, Tilburg, Nijmegen en Arnhem.";

    const cities = Object.entries(CITY_CONTENT).map(([slug, c]) => ({
      slug,
      name: c.name,
      url: `${SITE_URL}/groepenkast-vervangen/${slug}`,
    }));

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "keywords", content: `groepenkast vervangen, ${cities.map((c) => c.name).join(", ")}, Elvion Group Nederland` },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "nl_NL" },
        { property: "og:site_name", content: "Elvion Group Nederland" },
        { property: "og:image", content: OG_IMAGE },
        { property: "og:image:secure_url", content: OG_IMAGE },
        { property: "og:image:type", content: "image/jpeg" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "640" },
        { property: "og:image:alt", content: "Elvion Group Nederland — Groepenkast vervangen in heel Nederland" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: OG_IMAGE },
        { name: "twitter:image:alt", content: "Elvion Group Nederland — Groepenkast vervangen in heel Nederland" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
              { "@type": "ListItem", position: 2, name: "Dekking", item: url },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Steden waar Elvion Group Nederland groepenkasten vervangt",
            itemListElement: cities.map((c, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: `Groepenkast vervangen ${c.name}`,
              url: c.url,
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Groepenkast vervangen",
            serviceType: "Groepenkast vervangen",
            provider: {
              "@type": "Organization",
              name: "Elvion Group Nederland",
              url: SITE_URL,
              telephone: "+31850806695",
            },
            areaServed: cities.map((c) => ({ "@type": "City", name: c.name })),
            description,
          }),
        },
      ],
    };
  },
  component: DekkingPage,
});

function DekkingPage() {
  const cities = Object.entries(CITY_CONTENT).map(([slug, city]) => ({
    slug,
    name: city.name,
    neighborhoods: city.neighborhoods,
    href: `/groepenkast-vervangen/${slug}`,
  }));

  return (
    <>
      <Header variant="shop" />
      <main className="bg-white">
        {/* Hero */}
        <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <nav aria-label="Kruimelpad" className="mb-4 text-sm text-text-secondary">
            <Link to="/" className="hover:text-orange">Home</Link>
            <span className="mx-2 text-foreground/30">/</span>
            <span className="text-foreground">Dekking</span>
          </nav>

          <div className="max-w-3xl">
            <h1 className="font-display text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">
              Groepenkast vervangen in heel Nederland
            </h1>
            <p className="mt-4 text-base text-text-secondary sm:text-lg">
              Elvion Group Nederland vervangt dagelijks groepenkasten door erkende NEN 1010-installateurs.
              Kies hieronder uw stad voor lokale informatie, prijzen en een vrijblijvende offerte
              binnen 24 uur.
            </p>

            <ul className="mt-6 grid gap-2 sm:grid-cols-2">
              <li className="flex items-start gap-2 text-sm text-foreground/80">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-orange" />
                <span>15 jaar garantie op AEG-groepenkasten</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-foreground/80">
                <Clock className="mt-0.5 h-5 w-5 shrink-0 text-orange" />
                <span>Offerte binnen 24 uur, montage binnen 1 week</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-foreground/80">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-orange" />
                <span>Vaste prijs vooraf, geen meerwerk</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-foreground/80">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-orange" />
                <span>Zegelbevoegd bij Liander, Stedin en Enexis</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Cities grid */}
        <section aria-labelledby="steden-heading" className="mx-auto max-w-7xl px-4 pb-12 sm:px-6 lg:px-8 lg:pb-16">
          <h2 id="steden-heading" className="font-display text-2xl font-bold text-foreground sm:text-3xl">
            Onze werkgebieden
          </h2>
          <p className="mt-2 text-text-secondary">
            Klik op een stad voor prijzen, buurten en klantverhalen uit die regio.
          </p>

          <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {cities.map((city) => (
              <li key={city.slug}>
                <Link
                  to={city.href}
                  aria-label={`Groepenkast vervangen in ${city.name}`}
                  title={`Groepenkast vervangen ${city.name}`}
                  className="group flex h-full flex-col rounded-xl border border-border bg-card p-5 shadow-sm transition-colors hover:border-orange hover:bg-orange/5"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange/10 text-orange">
                        <MapPin className="h-5 w-5" />
                      </div>
                      <span className="font-display text-lg font-semibold text-foreground">
                        {city.name}
                      </span>
                    </div>
                    <ArrowRight className="h-5 w-5 text-foreground/40 transition-colors group-hover:text-orange" />
                  </div>
                  <p className="mt-3 text-sm text-text-secondary line-clamp-2">
                    Groepenkast vervangen in {city.name} en omstreken, o.a.{" "}
                    {city.neighborhoods.slice(0, 3).join(", ")}.
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        {/* Related links / SEO body */}
        <section aria-labelledby="regio-heading" className="border-t border-border bg-muted/30">
          <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
            <h2 id="regio-heading" className="font-display text-2xl font-bold text-foreground sm:text-3xl">
              Groepenkast vervangen per regio
            </h2>
            <p className="mt-3 max-w-3xl text-text-secondary">
              Van vooroorlogse woningen in de Randstad tot moderne nieuwbouw in Brabant en
              Gelderland: onze installateurs kennen de lokale netbeheerders en woningtypes.
              Bekijk hieronder de dedicated pagina voor uw stad.
            </p>

            <ul className="mt-6 flex flex-wrap gap-2">
              {cities.map((city) => (
                <li key={city.slug}>
                  <Link
                    to={city.href}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-4 py-2 text-sm font-medium text-foreground transition-colors hover:border-orange hover:text-orange"
                  >
                    Groepenkast vervangen {city.name}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-10 rounded-2xl border border-border bg-white p-6 sm:p-8">
              <h3 className="font-display text-xl font-bold text-foreground">
                Uw stad staat er niet tussen?
              </h3>
              <p className="mt-2 text-text-secondary">
                Wij werken door heel Nederland. Vraag een vrijblijvende offerte aan; u ontvangt
                binnen 24 uur reactie.
              </p>
              <Link
                to="/offerte-aanvragen" search={{ product: "groepenkast" }}
                className="mt-4 inline-flex items-center gap-2 rounded-lg bg-orange px-5 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
              >
                Offerte aanvragen
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
